import { Collapse, Space, Table, Tag } from '@faceup/ui-base'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'

const fragments = {
  EntitlementsCollapse_billing: graphql(`
    fragment EntitlementsCollapse_billing on Billing {
      id
      entitlements {
        id
        featureId
        enabled
        value
        type
      }
    }
  `),
}

type EntitlementsCollapseProps = {
  billing: FragmentType<typeof fragments.EntitlementsCollapse_billing>
}

export const EntitlementsCollapse = ({ billing: _billing }: EntitlementsCollapseProps) => {
  const billing = getFragmentData(fragments.EntitlementsCollapse_billing, _billing)
  const entitlements = billing.entitlements.toSorted((a, b) =>
    a.featureId.localeCompare(b.featureId)
  )
  const outOfSyncCount = entitlements.filter(({ enabled, value }) =>
    isOutOfSync(enabled, value)
  ).length

  return (
    <Collapse
      className='mt-5'
      ghost
      items={[
        {
          key: 'entitlements',
          label: (
            <Space>
              {`Entitlements (${entitlements.length})`}
              {outOfSyncCount > 0 && <Tag color='red'>{outOfSyncCount} out of sync</Tag>}
            </Space>
          ),
          children: (
            <Table
              rowKey='id'
              dataSource={entitlements}
              pagination={false}
              size='small'
              locale={{ emptyText: 'No entitlements' }}
              scroll={{ x: 'max-content' }}
              columns={[
                {
                  title: 'Feature',
                  key: 'featureId',
                  render: ({ featureId, type }) => (
                    <Space>
                      {featureId}
                      <Tag>{type}</Tag>
                    </Space>
                  ),
                },
                {
                  title: 'Enabled',
                  key: 'enabled',
                  render: ({ enabled }) => (
                    <Tag color={enabled ? 'green' : 'default'}>{enabled ? 'On' : 'Off'}</Tag>
                  ),
                },
                {
                  title: 'Value',
                  key: 'value',
                  render: ({ enabled, value }) => (
                    <EntitlementValue enabled={enabled} value={value} />
                  ),
                },
              ]}
            />
          ),
        },
      ]}
    />
  )
}

const isOutOfSync = (enabled: boolean, value: string) =>
  (value === 'true' || value === 'false') && value !== String(enabled)

type EntitlementValueProps = {
  enabled: boolean
  value: string
}

const EntitlementValue = ({ enabled, value }: EntitlementValueProps) => {
  if (value === String(enabled)) {
    return null
  }

  if (isOutOfSync(enabled, value)) {
    return <Tag color='red'>{value}</Tag>
  }

  return value
}
